import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DollarSign, TrendingUp, Package, Users, CreditCard, ArrowUpRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import {
    SACard, MetricCard, formatBytes, timeAgo,
} from "@/components/superadmin/SAPrimitives";

const formatUsd = (n) =>
    "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatUsdCents = (n) =>
    "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const UPGRADE_PLANS = [
    { id: "basic", label: "Basic", price: 49 },
    { id: "pro", label: "Pro", price: 149 },
    { id: "enterprise", label: "Enterprise", price: 499 },
];

export default function SuperAdminBilling() {
    const [billing, setBilling] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [err, setErr] = useState("");
    const [upgradeTenant, setUpgradeTenant] = useState(null);
    const [params, setParams] = useSearchParams();

    const load = useCallback(async () => {
        try {
            const { data } = await http.get("/superadmin/billing");
            setBilling(data);
            const inv = await http.get("/superadmin/invoices", { params: { limit: 40 } });
            setInvoices(inv.data.invoices || []);
        } catch (e) {
            setErr(errMsg(e));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Post-checkout return — poll status until resolved, then refresh.
    useEffect(() => {
        const sid = params.get("session_id");
        const canceled = params.get("checkout");
        if (canceled === "canceled") {
            toast.info("Checkout canceled.");
            const next = new URLSearchParams(params);
            next.delete("checkout");
            setParams(next, { replace: true });
            return;
        }
        if (!sid) return;

        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                const { data } = await http.get(`/payments/checkout/status/${sid}`);
                if (data.payment_status === "paid") {
                    toast.success(`Plan upgrade successful (${data.plan})`);
                    const next = new URLSearchParams(params);
                    next.delete("session_id");
                    setParams(next, { replace: true });
                    load();
                    return;
                }
                if (data.status === "expired" || attempts > 8) {
                    toast.error("Payment not completed — please try again.");
                    const next = new URLSearchParams(params);
                    next.delete("session_id");
                    setParams(next, { replace: true });
                    return;
                }
                setTimeout(poll, 2500);
            } catch (e) {
                toast.error(errMsg(e));
            }
        };
        toast.loading("Verifying payment…", { id: "poll", duration: 4000 });
        poll();
    }, [params, setParams, load]);

    if (err) return <SACard className="text-red-300" data-testid="sa-billing-error">{err}</SACard>;
    if (!billing) return <SACard data-testid="sa-billing-loading">Loading…</SACard>;

    const totalTenants = (billing.plans || []).reduce((a, p) => a + (p.tenants || 0), 0);
    const paidTenants = (billing.plans || [])
        .filter((p) => (p.price_usd_month || 0) > 0)
        .reduce((a, p) => a + (p.tenants || 0), 0);
    const paidRatio = totalTenants ? Math.round((paidTenants / totalTenants) * 100) : 0;
    const maxPlanMrr = Math.max(1, ...(billing.plans || []).map((p) => p.mrr_usd_month || 0));
    const rev30 = billing.revenue_actual?.revenue_30d_usd || { total: 0, count: 0 };
    const revAll = billing.revenue_actual?.revenue_all_time_usd || { total: 0, count: 0 };

    return (
        <div className="space-y-5" data-testid="sa-billing-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Billing</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        Stripe-backed plan upgrades · real invoices below · MRR estimate on top
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="sa-billing-metrics">
                <MetricCard
                    icon={DollarSign}
                    label="MRR Estimate"
                    value={formatUsd(billing.mrr_usd_month_estimate)}
                    sublabel="Book value · plan × tenants"
                    tone="good"
                    testId="sa-billing-mrr"
                />
                <MetricCard
                    icon={CreditCard}
                    label="Paid · 30d"
                    value={formatUsd(rev30.total)}
                    sublabel={`${rev30.count} invoice${rev30.count === 1 ? "" : "s"} · Stripe`}
                    tone="good"
                    testId="sa-billing-paid-30d"
                />
                <MetricCard
                    icon={TrendingUp}
                    label="ARR Estimate"
                    value={formatUsd(billing.arr_usd_year_estimate)}
                    sublabel="12 × current MRR"
                    tone="good"
                    testId="sa-billing-arr"
                />
                <MetricCard
                    icon={Users}
                    label="Paid Tenants"
                    value={paidTenants}
                    sublabel={`${paidRatio}% of ${totalTenants} workspaces`}
                />
            </div>

            <SACard data-testid="sa-billing-plans">
                <div className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                    Revenue by plan
                </div>
                <div className="space-y-3">
                    {(billing.plans || []).map((p) => {
                        const pct = (p.mrr_usd_month / maxPlanMrr) * 100;
                        return (
                            <div key={p.plan} data-testid={`sa-billing-plan-${p.plan}`}>
                                <div className="mb-1 flex items-center justify-between text-[12.5px]">
                                    <div className="flex items-center gap-2">
                                        <span className="rounded-sm bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/75">
                                            {p.plan}
                                        </span>
                                        <span className="text-white/60">
                                            {p.tenants} tenant{p.tenants === 1 ? "" : "s"} × {formatUsd(p.price_usd_month)}/mo
                                        </span>
                                    </div>
                                    <span className="font-semibold text-white/90">
                                        {formatUsd(p.mrr_usd_month)}<span className="text-[10px] text-white/45"> /mo</span>
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-white/[0.04]">
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${pct}%`,
                                            background:
                                                p.mrr_usd_month > 0
                                                    ? "linear-gradient(90deg, #004AAD, #00C2FF)"
                                                    : "rgba(255,255,255,0.12)",
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </SACard>

            <SACard className="p-0" data-testid="sa-billing-top-usage">
                <div className="flex items-center justify-between px-5 py-4">
                    <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Top usage · last 30 days
                    </div>
                    <div className="text-[11px] text-white/45">
                        Click <b>Upgrade</b> to start a Stripe checkout
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-y border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Tenant</th>
                                <th className="px-2 py-2.5 font-medium">Plan</th>
                                <th className="px-2 py-2.5 text-right font-medium">Price</th>
                                <th className="px-2 py-2.5 text-right font-medium">Data (30d)</th>
                                <th className="px-2 py-2.5 text-right font-medium">Jobs (30d)</th>
                                <th className="px-5 py-2.5 text-right" />
                            </tr>
                        </thead>
                        <tbody>
                            {(billing.top_usage || []).map((t) => (
                                <tr key={t.tenant_id} className="border-b border-white/5 hover:bg-white/[0.03]">
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/superadmin/tenants/${t.tenant_id}`}
                                            className="font-semibold text-white hover:text-[#00C2FF]"
                                        >
                                            {t.tenant_name || t.tenant_slug}
                                        </Link>
                                        <div className="text-[10.5px] text-white/45">{t.tenant_slug}</div>
                                    </td>
                                    <td className="px-2 py-3 uppercase text-white/70">{t.plan}</td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {formatUsd(t.plan_price_usd_month)}
                                        <span className="text-[10px] text-white/45"> /mo</span>
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {formatBytes(t.bytes_30d)}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">{t.jobs_30d}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button
                                            data-testid={`sa-billing-upgrade-${t.tenant_id}`}
                                            onClick={() =>
                                                setUpgradeTenant({
                                                    id: t.tenant_id,
                                                    name: t.tenant_name || t.tenant_slug,
                                                    plan: t.plan,
                                                })
                                            }
                                            className="inline-flex items-center gap-1 rounded-md border border-[#00C2FF]/40 bg-[#00C2FF]/10 px-2.5 py-1 text-[11px] font-semibold text-[#00C2FF] hover:bg-[#00C2FF]/20"
                                        >
                                            <ArrowUpRight size={11} /> Upgrade
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {(billing.top_usage || []).length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-8 text-center text-white/40">
                                        No completed jobs in this window.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>

            <SACard className="p-0" data-testid="sa-billing-invoices">
                <div className="flex items-center justify-between px-5 py-4">
                    <div>
                        <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                            Stripe invoices · all time
                        </div>
                        <div className="mt-0.5 text-[11px] text-white/45">
                            {invoices.length} record{invoices.length === 1 ? "" : "s"} · paid total {formatUsd(revAll.total)}
                        </div>
                    </div>
                    <button
                        data-testid="sa-billing-new-checkout"
                        onClick={() =>
                            setUpgradeTenant({
                                id: null,        // picker will surface tenants
                                name: "",
                                plan: null,
                            })
                        }
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#00C2FF]/40 bg-[#00C2FF]/10 px-3 py-1.5 text-[12px] font-semibold text-[#00C2FF] hover:bg-[#00C2FF]/20"
                    >
                        <CreditCard size={12} /> New checkout
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-y border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Tenant</th>
                                <th className="px-2 py-2.5 font-medium">Plan</th>
                                <th className="px-2 py-2.5 text-right font-medium">Amount</th>
                                <th className="px-2 py-2.5 font-medium">Status</th>
                                <th className="px-2 py-2.5 font-medium">Initiator</th>
                                <th className="px-5 py-2.5 font-medium">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                                    <td className="px-5 py-2.5">
                                        <Link
                                            to={`/superadmin/tenants/${inv.tenant_id}`}
                                            className="font-semibold text-white hover:text-[#00C2FF]"
                                        >
                                            {inv.tenant_name || inv.tenant_slug || inv.tenant_id?.slice(0, 8)}
                                        </Link>
                                    </td>
                                    <td className="px-2 py-2.5 uppercase text-white/70">{inv.plan}</td>
                                    <td className="px-2 py-2.5 text-right text-white/85">
                                        {formatUsdCents(inv.amount)}
                                    </td>
                                    <td className="px-2 py-2.5">
                                        <InvoiceStatus status={inv.payment_status} />
                                    </td>
                                    <td className="px-2 py-2.5 text-white/65">{inv.initiator_email || "—"}</td>
                                    <td className="px-5 py-2.5 text-white/55">{timeAgo(inv.created_at)}</td>
                                </tr>
                            ))}
                            {invoices.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-8 text-center text-white/40">
                                        No Stripe invoices yet — click <b>Upgrade</b> on a tenant above to start a checkout.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>

            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4 text-[11.5px] text-white/55">
                {billing.note}
            </div>

            {upgradeTenant && (
                <UpgradeModal
                    tenant={upgradeTenant}
                    onClose={() => setUpgradeTenant(null)}
                />
            )}
        </div>
    );
}

function InvoiceStatus({ status }) {
    const map = {
        paid: { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "paid" },
        unpaid: { bg: "bg-amber-500/15", fg: "text-amber-300", label: "unpaid" },
        complete: { bg: "bg-[#00C2FF]/15", fg: "text-[#00C2FF]", label: "complete" },
        expired: { bg: "bg-red-500/15", fg: "text-red-300", label: "expired" },
        initiated: { bg: "bg-white/10", fg: "text-white/70", label: "initiated" },
    };
    const m = map[status] || { bg: "bg-white/10", fg: "text-white/70", label: status || "—" };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${m.bg} ${m.fg}`}>
            {m.label}
        </span>
    );
}

function UpgradeModal({ tenant, onClose }) {
    const [plan, setPlan] = useState("pro");
    const [busy, setBusy] = useState(false);
    const [tenants, setTenants] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState(tenant?.id || "");
    const needsPicker = !tenant?.id;

    // Only fetch the tenant list when the modal was opened without a
    // preselected tenant (header-level "New checkout"). Keeps the tenant
    // row CTA zero-cost.
    useEffect(() => {
        if (!needsPicker) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await http.get("/superadmin/tenants", { params: { limit: 100 } });
                if (!alive) return;
                setTenants(data.tenants || []);
                if (!selectedTenantId && data.tenants?.length) {
                    setSelectedTenantId(data.tenants[0].id);
                }
            } catch (e) {
                // Empty tenant picker is a valid state for new
                // installs — render the page anyway. Log so the
                // failure isn't completely silent in DevTools.
                console.warn("superadmin-billing: tenant list fetch failed:", e?.message || e);
            }
        })();
        return () => { alive = false; };
    }, [needsPicker, selectedTenantId]);

    const targetTenantId = tenant?.id || selectedTenantId;
    const targetTenantName =
        tenant?.name ||
        tenants.find((t) => t.id === selectedTenantId)?.name ||
        "—";
    const targetCurrentPlan =
        tenant?.plan ||
        tenants.find((t) => t.id === selectedTenantId)?.plan ||
        "free";

    const submit = async () => {
        if (!targetTenantId) {
            toast.error("Select a tenant first");
            return;
        }
        setBusy(true);
        try {
            const { data } = await http.post("/payments/checkout/session", {
                plan,
                tenant_id: targetTenantId,
                origin_url: window.location.origin,
            });
            if (data.url) {
                window.location.href = data.url;
                return;
            }
            toast.error("Stripe did not return a checkout URL");
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            data-testid="sa-billing-upgrade-modal"
        >
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0a142b] p-5 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10.5px] uppercase tracking-wider text-white/45">Plan upgrade</div>
                        <div className="mt-0.5 text-[15px] font-semibold">{targetTenantName}</div>
                        <div className="mt-0.5 text-[11px] text-white/55">Current plan: {targetCurrentPlan}</div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="sa-billing-upgrade-close"
                        className="rounded-md border border-white/10 p-1.5 text-white/55 hover:bg-white/5"
                    >
                        <X size={13} />
                    </button>
                </div>

                {needsPicker && (
                    <label className="mt-4 block">
                        <span className="mb-1 block text-[10.5px] uppercase tracking-wider text-white/45">
                            Tenant
                        </span>
                        <select
                            data-testid="sa-billing-upgrade-tenant-picker"
                            value={selectedTenantId}
                            onChange={(e) => setSelectedTenantId(e.target.value)}
                            className="h-9 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-[12.5px] text-white focus:border-[#00C2FF]/50 focus:outline-none"
                        >
                            {tenants.map((t) => (
                                <option key={t.id} value={t.id} className="bg-[#0a142b]">
                                    {t.name} · {t.plan || "free"} · {t.slug || t.id.slice(0, 8)}
                                </option>
                            ))}
                            {tenants.length === 0 && <option value="">Loading…</option>}
                        </select>
                    </label>
                )}

                <div className="mt-4 grid grid-cols-3 gap-2">
                    {UPGRADE_PLANS.map((p) => {
                        const active = plan === p.id;
                        return (
                            <button
                                key={p.id}
                                onClick={() => setPlan(p.id)}
                                data-testid={`sa-billing-upgrade-plan-${p.id}`}
                                className={[
                                    "rounded-md border px-3 py-3 text-left transition-colors",
                                    active
                                        ? "border-[#00C2FF]/60 bg-[#00C2FF]/10"
                                        : "border-white/10 bg-white/[0.02] hover:border-white/25",
                                ].join(" ")}
                            >
                                <div className="text-[10px] uppercase tracking-wider text-white/50">{p.label}</div>
                                <div className="mt-1 text-[15px] font-semibold">
                                    ${p.price}<span className="text-[10px] text-white/45"> /mo</span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-4 rounded-md border border-white/5 bg-white/[0.02] p-3 text-[11.5px] text-white/60">
                    One-time charge via Stripe test mode · card <code className="text-white/85">4242 4242 4242 4242</code>, any future expiry, any CVC, any ZIP.
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        data-testid="sa-billing-upgrade-cancel"
                        className="rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy || !targetTenantId}
                        data-testid="sa-billing-upgrade-submit"
                        className="inline-flex items-center gap-1.5 rounded-md bg-[#00C2FF] px-3 py-1.5 text-[12px] font-semibold text-[#041124] hover:brightness-110 disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                        {busy ? "Opening Stripe…" : "Continue to checkout"}
                    </button>
                </div>
            </div>
        </div>
    );
}
